import { useEffect, useState } from "react";
import { http, apiError } from "@/lib/api";
import { Trash, Phone, ChatCircle, EnvelopeSimple, Tag } from "@phosphor-icons/react";

/**
 * CoachWaitlistPage — coach-only view of waitlist signups from /waitlist.
 *
 * Lets Jimmy:
 *  - Scan all leads sorted newest-first
 *  - Filter by status (new / contacted / converted / lost)
 *  - Quick-launch SMS / phone / email straight to the lead
 *  - Update lead status + leave a private coach note
 *  - Delete bogus leads
 */
const STATUS_OPTIONS = [
  { key: "new", label: "New", color: "#FF3333" },
  { key: "contacted", label: "Contacted", color: "#EAB308" },
  { key: "converted", label: "Converted", color: "#22C55E" },
  { key: "lost", label: "Lost", color: "#71717A" },
];

function StatusPill({ status, onChange, leadId }) {
  const cur = STATUS_OPTIONS.find((s) => s.key === status) || STATUS_OPTIONS[0];
  return (
    <select
      value={status}
      onChange={(e) => onChange(leadId, e.target.value)}
      data-testid={`lead-status-${leadId}`}
      className="bg-[#0A0A0A] border px-2.5 py-1 text-xs font-oswald uppercase tracking-wide cursor-pointer"
      style={{ borderColor: cur.color, color: cur.color }}
    >
      {STATUS_OPTIONS.map((s) => (
        <option key={s.key} value={s.key}>{s.label}</option>
      ))}
    </select>
  );
}

function LeadRow({ lead, onStatus, onDelete }) {
  const tel = (lead.mobile || "").replace(/[^0-9+]/g, "");
  const dateStr = lead.created_at ? new Date(lead.created_at).toLocaleString() : "";
  return (
    <div className="border border-[#262626] p-4 sm:p-5 bg-[#0A0A0A] hover:border-zinc-500 transition-colors" data-testid={`lead-row-${lead.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-oswald text-lg uppercase tracking-wide text-white leading-tight truncate">{lead.name}</div>
          <div className="text-xs text-zinc-500 mt-0.5">{dateStr}</div>
        </div>
        <div className="flex items-center gap-2">
          <StatusPill status={lead.status} onChange={onStatus} leadId={lead.id} />
          <button
            onClick={() => onDelete(lead.id, lead.name)}
            className="text-zinc-600 hover:text-[#FF3333] transition-colors p-1"
            data-testid={`lead-delete-${lead.id}`}
            aria-label="Delete lead"
            title="Delete"
          >
            <Trash size={16} weight="bold" />
          </button>
        </div>
      </div>

      <div className="space-y-2 mb-3">
        <a href={`mailto:${lead.email}`} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-[#FF3333] transition-colors break-all" data-testid={`lead-email-${lead.id}`}>
          <EnvelopeSimple size={14} weight="bold" className="text-zinc-500 shrink-0" />
          {lead.email}
        </a>
        <a href={`tel:${tel}`} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-[#FF3333] transition-colors" data-testid={`lead-mobile-${lead.id}`}>
          <Phone size={14} weight="bold" className="text-zinc-500 shrink-0" />
          {lead.mobile}
        </a>
      </div>

      <div className="border-t border-[#1A1A1A] pt-3">
        <div className="id-label text-zinc-500 mb-1">Goal</div>
        <div className="text-sm text-zinc-200 leading-relaxed whitespace-pre-wrap">{lead.weight_loss_goal}</div>
      </div>

      {lead.source && (
        <div className="flex items-center gap-1.5 mt-3 text-xs text-zinc-600">
          <Tag size={12} weight="bold" />
          via {lead.source}
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <a
          href={`sms:${tel}?&body=${encodeURIComponent(`Hey ${(lead.name || "").split(" ")[0]}, it's Jimmy from Lazy Lean Method. Got your waitlist signup — got a quick question for you.`)}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-oswald uppercase tracking-wide py-2 border border-[#FF3333] text-[#FF3333] hover:bg-[#FF3333] hover:text-white transition-colors"
          data-testid={`lead-sms-${lead.id}`}
        >
          <ChatCircle size={12} weight="bold" /> Text
        </a>
        <a
          href={`tel:${tel}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-oswald uppercase tracking-wide py-2 border border-[#262626] text-zinc-300 hover:border-[#FF3333] hover:text-[#FF3333] transition-colors"
          data-testid={`lead-call-${lead.id}`}
        >
          <Phone size={12} weight="bold" /> Call
        </a>
        <a
          href={`mailto:${lead.email}?subject=${encodeURIComponent("Your Lazy Lean waitlist signup")}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-oswald uppercase tracking-wide py-2 border border-[#262626] text-zinc-300 hover:border-[#FF3333] hover:text-[#FF3333] transition-colors"
          data-testid={`lead-email-btn-${lead.id}`}
        >
          <EnvelopeSimple size={12} weight="bold" /> Email
        </a>
      </div>
    </div>
  );
}

export default function CoachWaitlistPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async (status = "") => {
    setLoading(true);
    try {
      const params = status ? { status } : {};
      const { data } = await http.get("/coach/waitlist", { params });
      setItems(data.items || []);
      setCounts(data.counts || {});
      setErr("");
    } catch (e) { setErr(apiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(filter); }, [filter]);

  const onStatus = async (leadId, status) => {
    try {
      await http.patch(`/coach/waitlist/${leadId}`, { status });
      await load(filter);
    } catch (e) { setErr(apiError(e)); }
  };

  const onDelete = async (leadId, name) => {
    if (!window.confirm(`Delete waitlist lead ${name}? This can't be undone.`)) return;
    try {
      await http.delete(`/coach/waitlist/${leadId}`);
      await load(filter);
    } catch (e) { setErr(apiError(e)); }
  };

  const total = (counts.new || 0) + (counts.contacted || 0) + (counts.converted || 0) + (counts.lost || 0);

  return (
    <div className="max-w-6xl mx-auto p-5 sm:p-8">
      <div className="mb-6">
        <div className="id-label text-[#FF3333] mb-2">— Waitlist —</div>
        <h1 className="font-oswald text-3xl sm:text-4xl uppercase tracking-tight text-white">Pre-launch leads</h1>
        <p className="text-zinc-500 text-sm font-manrope mt-2">
          {total} total · share your IG link → <code className="text-zinc-300">lazyleanmethod.com/waitlist</code>
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="waitlist-filters">
        <button
          onClick={() => setFilter("")}
          className={`px-3 py-1.5 text-xs font-oswald uppercase tracking-wide border transition-colors ${filter === "" ? "border-[#FF3333] text-[#FF3333] bg-[#FF3333]/10" : "border-[#262626] text-zinc-400 hover:border-zinc-500"}`}
          data-testid="filter-all"
        >
          All ({total})
        </button>
        {STATUS_OPTIONS.map((s) => (
          <button
            key={s.key}
            onClick={() => setFilter(s.key)}
            className={`px-3 py-1.5 text-xs font-oswald uppercase tracking-wide border transition-colors ${filter === s.key ? "text-white" : "text-zinc-400 hover:text-white"}`}
            style={{
              borderColor: filter === s.key ? s.color : "#262626",
              backgroundColor: filter === s.key ? `${s.color}22` : "transparent",
              color: filter === s.key ? s.color : undefined,
            }}
            data-testid={`filter-${s.key}`}
          >
            {s.label} ({counts[s.key] || 0})
          </button>
        ))}
      </div>

      {err && (
        <div className="border border-[#FF3333] bg-[#FF3333]/10 px-3 py-2 text-sm text-[#FF3333] mb-4" data-testid="waitlist-error">
          {err}
        </div>
      )}

      {loading && items.length === 0 ? (
        <div className="text-zinc-500 text-sm py-8 text-center">Loading...</div>
      ) : items.length === 0 ? (
        <div className="border border-[#262626] bg-[#0A0A0A] p-10 text-center" data-testid="waitlist-empty">
          <div className="text-zinc-500 font-manrope">
            {filter ? `No leads with status "${filter}" yet.` : "No leads yet. Share your IG link to start collecting."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="waitlist-list">
          {items.map((lead) => (
            <LeadRow key={lead.id} lead={lead} onStatus={onStatus} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  );
}
